// src/pages/Pricing.jsx

import {
    Box,
    Button,
    Container,
    Group,
    Paper,
    Stack,
    Text,
    Title,
    Grid,
    Badge,
    SimpleGrid,
    Image,
    List,
    ThemeIcon,
} from "@mantine/core";

export default function Pricing() {

    const pricingPlans = [
        {
            title: "1 Month",
            price: "₹499",
            desc: "Best for trying RentalFlow for your rental business.",
            badge: "Starter",
        },
        {
            title: "6 Months",
            price: "₹2,499",
            desc: "Good for shops that want to use RentalFlow for a longer period.",
            badge: "Popular",
        },
        {
            title: "12 Months",
            price: "₹3,999",
            desc: "Best value for rental shops that want yearly access.",
            badge: "Best Value",
        },
    ];

    const oneTimePlan = {
        title: "One-Time Fee",
        price: "₹9,999",
        desc: "Limited-time offer for rental owners who want long-term access without monthly renewal.",
        badge: "Limited Time",
    };

    return (
        <Box
            style={{
                minHeight: "100vh",
                backgroundColor: "#f0ede8",
            }}
        >       
            {/* Pricing Section */}
            <Box
                id="pricing"
                py={40}
                style={{
                    backgroundColor: "#111",
                }}
            >
                <Container size="lg">
                    <Stack align="center" mb={50}>
                        <Badge color="orange" variant="light">
                            Subscription Plans
                        </Badge>

                        <Title order={2} fw={900} c="white" ta="center">
                            Simple pricing for rental shops
                        </Title>

                        <Text
                            ta="center"
                            c="#bbb"
                            maw={720}
                            style={{ lineHeight: 1.8 }}
                        >
                            Choose a plan based on how long you want to use RentalFlow for
                            your bike or car rental business.
                        </Text>
                    </Stack>

                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={24}>
                        {pricingPlans.map((plan) => (
                            <Paper
                                key={plan.title}
                                radius={26}
                                p={30}
                                bg="#1b1b1b"
                                style={{
                                    border:
                                        plan.badge === "Popular"
                                            ? "1px solid #f24c1a"
                                            : "1px solid #2a2a2a",
                                    height: "100%",
                                }}
                            >
                                <Stack gap={14}>
                                    <Badge
                                        color="orange"
                                        variant={plan.badge === "Popular" ? "filled" : "light"}
                                        radius="xl"
                                        w="fit-content"
                                    >
                                        {plan.badge}
                                    </Badge>

                                    <Title order={3} c="white" fw={900}>
                                        {plan.title}
                                    </Title>

                                    <Group align="end" gap={6}>
                                        <Text
                                            c="#f24c1a"
                                            fw={900}
                                            style={{
                                                fontSize: "42px",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {plan.price}
                                        </Text>

                                        <Text c="#aaa" fw={700}>
                                            / plan
                                        </Text>
                                    </Group>

                                    <Text c="#bdbdbd" size="sm" style={{ lineHeight: 1.7 }}>
                                        {plan.desc}
                                    </Text>

                                    <List spacing="sm" c="#ccc" size="sm">
                                        <List.Item>QR-based booking system</List.Item>
                                        <List.Item>Customer document collection</List.Item>
                                        <List.Item>Booking and fleet management</List.Item>
                                        <List.Item>Pre-ride vehicle photo proof</List.Item>
                                    </List>
                                </Stack>
                            </Paper>
                        ))}
                    </SimpleGrid>

                    <Paper
                        mt={28}
                        radius={28}
                        p={{ base: 28, md: 36 }}
                        bg="#1b1b1b"
                        style={{
                            border: "1px solid #f24c1a",
                            boxShadow: "0 20px 70px rgba(242,76,26,0.12)",
                        }}
                    >
                        <Grid align="center" gutter={28}>
                            <Grid.Col span={{ base: 12, md: 8 }}>
                                <Stack gap={12}>
                                    <Badge color="orange" variant="filled" radius="xl" w="fit-content">
                                        {oneTimePlan.badge}
                                    </Badge>

                                    <Title order={3} c="white" fw={900}>
                                        {oneTimePlan.title}
                                    </Title>

                                    <Text c="#bdbdbd" style={{ lineHeight: 1.8 }}>
                                        {oneTimePlan.desc}
                                    </Text>
                                </Stack>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Stack align={{ base: "flex-start", md: "flex-end" }} gap={12}>
                                    <Text
                                        c="#f24c1a"
                                        fw={900}
                                        style={{
                                            fontSize: "46px",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {oneTimePlan.price}
                                    </Text>

                                    <Button
                                        component="a"
                                        href="tel:+918172818877"
                                        radius={14}
                                        size="md"
                                        style={{
                                            backgroundColor: "#f24c1a",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Contact for Offer
                                    </Button>
                                </Stack>
                            </Grid.Col>
                        </Grid>
                    </Paper>
                </Container>
            </Box>
            {/* CTA Section */}
            <Box py={70}>
                <Container size="md">
                    <Paper
                        radius={28}
                        p={{ base: 28, md: 42 }}
                        bg="white"
                        shadow="xs"
                        style={{
                            border: "1px solid #ece7df",
                        }}
                    >
                        <Stack align="center" gap={14}>
                            <Badge color="orange" variant="light">
                                RentalFlow
                            </Badge>

                            <Title order={2} fw={900} c="#1a1a1a" ta="center">
                                Ride More. Earn More.
                            </Title>

                            <Text
                                ta="center"
                                c="#666"
                                maw={650}
                                style={{ lineHeight: 1.8 }}
                            >
                                Give your rental shop a digital booking system
                                that helps you reduce paperwork, verify
                                customers, manage bookings, organize vehicles,
                                and protect your business with photo records.
                            </Text>

                            <Group mt={12} gap={18} justify="center">
                                <Button
                                    component="a"
                                    href="https://instagram.com/rentalflow.in"
                                    target="_blank"
                                    radius={14}
                                    size="md"
                                    style={{
                                        backgroundColor: "#f24c1a",
                                        fontWeight: 700,
                                    }}
                                >
                                    Partner With RentalFlow
                                </Button>

                                <Button
                                    component="a"
                                    href="https://rentalflow.in"
                                    target="_blank"
                                    radius={14}
                                    size="md"
                                    variant="outline"
                                    color="orange"
                                >
                                    Visit rentalflow.in
                                </Button>
                            </Group>

                            <Group mt={12} gap={18} justify="center">
                                <Text
                                    component="a"
                                    href="https://instagram.com/rentalflow.in"
                                    target="_blank"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    Instagram: @rentalflow.in
                                </Text>
                                <Text
                                    component="a"
                                    href="tel:+918172818877"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    Contact: 8172818877
                                </Text>

                                <Text
                                    component="a"
                                    href="https://rentalflow.in"
                                    target="_blank"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    rentalflow.in
                                </Text>
                            </Group>

                            <Group mt={6} gap={18} justify="center">
                            <Text
                                component="a"
                                href="/policy"
                                size="sm"
                                c="#777"
                                style={{ textDecoration: "none" }}
                            >
                                Privacy Policy
                            </Text>

                            <Text
                                component="a"
                                href="/terms"
                                size="sm"
                                c="#777"
                                style={{ textDecoration: "none" }}
                            >
                                Terms & Conditions
                            </Text>
                            </Group>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            {/* Footer */}
            <Box py={24}>
                <Container>
                    <Text ta="center" size="sm" c="#777">
                        © {new Date().getFullYear()} RentalFlow · Smart Rentals Made Simple
                    </Text>
                </Container>
            </Box>
        </Box>
    );
}